import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, CalendarClock } from 'lucide-react';
import { getMyAvailability } from '../../services/studentAvailability';

// Pilot-feedback follow-up (docs/DECISIONS.md, "Availability reminder modal") -
// students who register but never fill in their weekly Availability silently
// never get a Smart Study Plan, with nothing telling them why. A single
// dismissible modal, shown once per login session, nudges them toward
// /availability. Backdrop/panel/close-button styling adapted from
// SettingsModal.jsx (itself adapted from LoginModal.jsx) - the two existing
// modal precedents in this app.
//
// Mounted in StudentLayoutContent (StudentLayout.jsx) - the one component
// already wrapping every logged-in, non-admin route - so it fires exactly
// once per session regardless of which page the student lands on after
// login, the same way AgentLexaWidget/FeatureVisibilityProvider are wired
// there. No persistence (localStorage/sessionStorage) is used for the
// dismissed state - a plain in-memory flag is enough for "once per login
// session" (a fresh page load/login naturally resets it), and this is too
// low-stakes to warrant tracking beyond that.
export default function AvailabilityReminderModal() {
  const navigate = useNavigate();
  const [shouldShow, setShouldShow] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // Fails closed on any error - a non-critical nudge should never block
    // or disrupt navigation (same convention FeatureVisibilityContext
    // follows for its own fetch). getMyAvailability() itself already
    // resolves a non-ok response to {slots: [], locked: false} rather than
    // throwing, so this only needs to guard against a genuine network
    // failure (fetch rejecting).
    getMyAvailability()
      .then((data) => {
        if (!cancelled && Array.isArray(data?.slots) && data.slots.length === 0) {
          setShouldShow(true);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!shouldShow || dismissed) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setDismissed(true);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [shouldShow, dismissed]);

  if (!shouldShow || dismissed) return null;

  const handleFillNow = () => {
    setDismissed(true);
    navigate('/availability');
  };

  return (
    <div
      className="fixed top-0 right-0 bottom-0 left-0 z-[10001] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={() => setDismissed(true)}
    >
      <div
        className="bg-white border border-gray-200 rounded-dialog shadow-brand-lg relative w-full max-w-md p-6 sm:p-8"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          onClick={() => setDismissed(true)}
          className="absolute top-4 right-4 text-text-slate hover:text-navy transition-colors"
          aria-label="Close availability reminder"
        >
          <X size={22} />
        </button>

        <div className="flex items-center justify-center mb-5">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-blue/10 text-brand-blue">
            <CalendarClock size={22} />
          </span>
        </div>

        <h2 className="text-lg font-bold text-navy text-center mb-2">Set your weekly availability</h2>
        <p className="text-sm text-text-slate text-center mb-6">
          Let us know when you're free to study each week and we'll build you a personalized Smart Study Plan -
          it only takes a minute.
        </p>

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            type="button"
            onClick={() => setDismissed(true)}
            className="flex-1 rounded-btn px-4 py-2.5 text-sm font-semibold bg-surface-light hover:bg-gray-200 text-navy transition-colors"
          >
            Maybe later
          </button>
          <button
            type="button"
            onClick={handleFillNow}
            className="flex-1 rounded-btn px-4 py-2.5 text-sm font-semibold bg-brand-blue text-white hover:bg-brand-blue/90 transition-colors"
          >
            Fill Availability
          </button>
        </div>
      </div>
    </div>
  );
}
